import React, { useState, useMemo, useEffect } from 'react'
import { api, formatCurrency } from '../../utils/api'
import MarketPriceSection from './MarketPriceSection'
import SearchLinks from '../shared/SearchLinks'
import { shopifyImageUrls } from './productImages'
import ReverseLookupPopup from '../deals/quote-assessment/ReverseLookupPopup'

// Manual D2C reprice modal (spec §5). Edits the Shopify price and/or compare-at;
// the worker writes through to Shopify and eBay/Reverb follow at the rule %.
// Validation here mirrors the authoritative server-side checks — the server is
// the source of truth for a prod price write (SEC2).

const BLOCK_MSG = 'Either set a higher compare-at price OR delete the value.'

// Pounds string -> integer pence, or null when blank/invalid.
function toPence(str) {
  if (str == null || String(str).trim() === '') return null
  const n = Number.parseFloat(str)
  if (!Number.isFinite(n)) return null
  return Math.round(n * 100)
}

function toPounds(cents) {
  return cents == null ? '' : (cents / 100).toFixed(2)
}

export default function EditPriceModal({ product, onClose, onSaved }) {
  const currentCents = product?.byChannel?.shopify?.current_cents ?? null
  const existingCompareAtCents = product?.compare_at_cents ?? null
  const costCents = product?.cost_cents ?? null

  // Per §5: both fields pre-fill with the CURRENT PRICE; what's in the field is
  // exactly what gets written (clearing compare-at => null => off sale).
  const [newPrice, setNewPrice] = useState(toPounds(currentCents))
  const [compareAt, setCompareAt] = useState(toPounds(currentCents))
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState(null)
  const [shopImages, setShopImages] = useState([])   // Shopify image URLs (F1/F2)
  const [lightbox, setLightbox] = useState(null)      // enlarged image URL, or null
  const [identifyOpen, setIdentifyOpen] = useState(false)  // reverse-image identify popup (F2)

  // Pull the item's Shopify photos so the operator can eyeball what they're
  // pricing (F1) and feed the identify picker (F2). A failed fetch shows no
  // strip; the modal still works.
  useEffect(() => {
    let alive = true
    const id = product?.shopify_product_id
    if (!id) return
    api.get(`/api/inventory/shopify/product/${id}`)
      .then(r => { if (alive) setShopImages(shopifyImageUrls(r?.product)) })
      .catch(() => { if (alive) setShopImages([]) })
    return () => { alive = false }
  }, [product?.shopify_product_id])

  const newPriceCents = toPence(newPrice)
  const compareAtCents = toPence(compareAt) // null when the field is blank (=> clear)
  // The field pre-fills with the current price, so it differs from the existing
  // Shopify compare-at by default — surface that we're proposing a change and
  // offer a one-click revert to the existing value.
  const compareAtChanged = compareAtCents !== existingCompareAtCents

  // Hard block (client mirror of the server): new price must not exceed a
  // non-empty compare-at. No constraint when compare-at is blank.
  const blocked = useMemo(
    () => compareAtCents != null && newPriceCents != null && newPriceCents > compareAtCents,
    [compareAtCents, newPriceCents],
  )
  const invalid = newPriceCents == null || newPriceCents <= 0 || blocked

  async function handleSave() {
    setError(null)
    if (invalid) return

    // Non-blocking confirms (warn, allow): below cost + large swing.
    if (costCents != null && newPriceCents < costCents) {
      if (!window.confirm(
        `${formatCurrency(newPriceCents / 100)} is below cost ${formatCurrency(costCents / 100)} — proceed?`,
      )) return
    }
    if (currentCents) {
      const swing = Math.abs(newPriceCents - currentCents) / currentCents
      if (swing >= 0.2) {
        const pct = Math.round(swing * 100)
        if (!window.confirm(
          `${formatCurrency(currentCents / 100)} → ${formatCurrency(newPriceCents / 100)} is ${pct}% — sure?`,
        )) return
      }
    }
    // Saving flips BEFORE the first await: the floor lookup below is async, and
    // a second click landing in that window would double-POST the reprice. A
    // declined confirm returns through the finally, so the button re-enables.
    setSaving(true)
    try {
      // D15 sibling confirm: this pin path writes via /api/pricing/reprice, which
      // the inventory endpoints' server check does not cover. Consignment items
      // are engine-skipped so they rarely appear here, but a pinned price below
      // the seller's agreed floor still needs the warn-and-confirm (£-rendered,
      // D15b). Lookup failures skip the check rather than blocking the save.
      try {
        const resp = await api.get(`/api/inventory-locations/items?q=${encodeURIComponent(product.shopify_product_id)}&limit=5`)
        const rows = Array.isArray(resp) ? resp : (resp?.items || [])
        const row = rows.find(r => String(r.shopify_product_id) === String(product.shopify_product_id))
        if (row?.ownership === 'consignment' && row.floor_pct != null && row.initial_listing_price_cents != null) {
          const floorCents = Math.round(row.initial_listing_price_cents * row.floor_pct / 100)
          if (newPriceCents < floorCents) {
            if (!window.confirm(
              `${formatCurrency(newPriceCents / 100)} is below the ${formatCurrency(floorCents / 100)} floor the seller agreed to `
              + `(${row.floor_pct}% of the ${formatCurrency(row.initial_listing_price_cents / 100)} agreed listing price) `
              + '— confirm you have contacted them.',
            )) return
          }
        }
      } catch (e) {
        console.error('Consignment floor lookup failed; saving without the floor check:', e)
      }

      // Blank compare-at field => explicit clear (take off sale). Otherwise send
      // the value. No actor — the worker injects it from the session (SEC1).
      const clear = compareAtCents == null
      await api.post('/api/pricing/reprice', {
        shopify_product_id: product.shopify_product_id,
        new_price_cents: newPriceCents,
        compare_at_cents: clear ? null : compareAtCents,
        clear_compare_at: clear,
      })
      onSaved?.()
      onClose?.()
    } catch (e) {
      setError(e.message || 'Failed to reprice')
    } finally {
      setSaving(false)
    }
  }

  async function handleReturnToCurve() {
    setError(null)
    if (!window.confirm(
      'Remove the fixed price pin and hand this item back to the automatic pricing curve?\n\n' +
      "The live price won't change right now (the curve is in preview). The “Fixed” tag clears, " +
      'and the curve will manage this item’s price once it goes live.',
    )) return
    setSaving(true)
    try {
      await api.post('/api/pricing/return-to-curve', {
        shopify_product_id: product.shopify_product_id,
      })
      onSaved?.()
      onClose?.()
    } catch (e) {
      setError(e.message || 'Failed to return to curve')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" style={{ maxWidth: 460 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h3 style={{ margin: 0 }}>Edit price</h3>
          <button className="modal-close" onClick={onClose} aria-label="Close">×</button>
        </div>

        <div className="modal-body-scroll">
          {shopImages.length > 0 && (
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
              {shopImages.map((src, i) => (
                <button key={src} type="button"
                  style={{ padding: 0, border: '1px solid var(--border-color)', borderRadius: 4, cursor: 'zoom-in', background: 'none', lineHeight: 0, flex: '0 0 auto' }}
                  onClick={() => setLightbox(src)} aria-label={`Photo ${i + 1}`}>
                  <img src={src} alt={`Item photo ${i + 1}`} style={{ width: 56, height: 56, objectFit: 'cover', display: 'block', borderRadius: 4 }} />
                </button>
              ))}
            </div>
          )}
          <div style={{ marginBottom: 12, fontWeight: 500 }}>{product?.title || product?.shopify_product_id}</div>
          <div style={{ color: '#6b7280', fontSize: 12, marginBottom: 16 }}>
            Current D2C: {formatCurrency(currentCents != null ? currentCents / 100 : null)}
            {'  ·  '}Cost: {formatCurrency(costCents != null ? costCents / 100 : null)}
            {'  ·  '}eBay/Reverb follow at the rule %.
          </div>

          {product?.has_override && (
            <div style={{ background: 'rgba(168, 85, 247, 0.12)', color: '#a855f7', padding: '8px 10px', borderRadius: 4, fontSize: 12, marginBottom: 16 }}>
              🔒 <strong>Fixed price</strong> — this item is pinned, so the automatic pricing curve is ignored for it.
              Saving a new price re-pins it; <strong>Return to curve</strong> removes the pin and hands it back to the curve.
            </div>
          )}

          <div className="form-group">
            <label>New price (£)</label>
            <input
              type="number" step="0.01" min="0" inputMode="decimal"
              value={newPrice} onChange={e => setNewPrice(e.target.value)} autoFocus
            />
          </div>

          <div className="form-group">
            <label>Compare-at price (£)</label>
            <input
              type="number" step="0.01" min="0" inputMode="decimal"
              value={compareAt} onChange={e => setCompareAt(e.target.value)}
              placeholder="blank = remove (take off sale)"
            />
            <div style={{ fontSize: 14, color: '#374151', marginTop: 6 }}>
              Existing on Shopify:{' '}
              <strong>{existingCompareAtCents != null ? formatCurrency(existingCompareAtCents / 100) : '— (not on sale)'}</strong>
              {compareAtChanged && (
                <span style={{ color: '#d97706', fontWeight: 600, marginLeft: 8 }}>
                  · proposing a change
                </span>
              )}
            </div>
            <div style={{ fontSize: 11, color: '#6b7280', marginTop: 4 }}>
              Setting a compare-at puts the item in the SALE collection; clearing it takes it out.
            </div>
            <div style={{ display: 'flex', gap: 8, marginTop: 6 }}>
              {existingCompareAtCents != null && compareAtChanged && (
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setCompareAt(toPounds(existingCompareAtCents))}>
                  Keep current compare-at price
                </button>
              )}
              {compareAt !== '' && (
                <button type="button" className="btn btn-sm btn-secondary" onClick={() => setCompareAt('')}>
                  Clear
                </button>
              )}
            </div>
          </div>

          {blocked && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{BLOCK_MSG}</div>}
          {error && <div style={{ color: '#ef4444', fontSize: 12, marginTop: 8 }}>{error}</div>}

          {/* Market pricing — research links + the reference lookup, one section
              (layout per operator 2026-07-14). */}
          <div style={{ borderTop: '1px solid var(--border-color)', marginTop: 12, paddingTop: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 10 }}>Market pricing</div>
            <div style={{ fontWeight: 600, fontSize: 12, color: '#6b7280', marginBottom: 6 }}>Research</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
              <SearchLinks
                title={product?.title}
                order={['google', 'ebaySold', 'ebayActive', 'reverbSold']}
              />
              <button type="button" className="btn btn-sm btn-secondary"
                title="Identify item — reverse-image check that our catalogue is right"
                aria-label="Identify item"
                disabled={shopImages.length === 0}
                onClick={() => setIdentifyOpen(true)}>📷</button>
            </div>
            <MarketPriceSection productId={product.shopify_product_id} title={product?.title} embedded />
          </div>
        </div>

        <div className="modal-footer" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          {product?.has_override ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              <button
                type="button" className="btn btn-sm btn-secondary"
                disabled={saving} onClick={handleReturnToCurve}
                title="Remove the manual price pin so the automatic pricing curve controls this item again"
              >
                Return to curve
              </button>
              <span style={{ fontSize: 10, color: '#9ca3af' }}>Clears the pin · live price unchanged now</span>
            </div>
          ) : (
            <span style={{ fontSize: 11, color: '#9ca3af' }}>Not pinned — follows the curve</span>
          )}
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" className="btn btn-sm btn-secondary" disabled={saving} onClick={onClose}>
              Cancel
            </button>
            <button type="button" className="btn btn-sm btn-primary" disabled={saving || invalid} onClick={handleSave}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>

        {lightbox && (
          <div className="modal-overlay" style={{ zIndex: 60 }} onClick={() => setLightbox(null)}>
            <img src={lightbox} alt="Item photo" style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 6 }} onClick={e => e.stopPropagation()} />
          </div>
        )}

        {identifyOpen && (
          <ReverseLookupPopup
            item={{ name: product?.title }}
            images={shopImages}
            itemPhotos={shopImages.slice(0, 4).map(src => ({ r2_key: src }))}  // preselect first 4
            lookupPath={`/api/pricing/${product.shopify_product_id}/identify`}
            onClose={() => setIdentifyOpen(false)}
          />
        )}
      </div>
    </div>
  )
}
